import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { KpiTile, Pill, SectionCard } from "../../components/Ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
export function formatQty(value) {
  return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 3 });
}

export function formatCurrency(value) {
  return `₹ ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

/** Always 2 decimal places — for financial adjustment / settlement money columns. */
export function formatMoney2(value) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return `₹ ${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Tailwind class for signed money: − red; + / zero default ink. */
export function moneyToneClass(value) {
  const num = Number(value);
  if (value == null || value === "" || Number.isNaN(num) || num >= 0) return "text-foreground";
  return "text-red-600";
}

export function formatPct(value) {
  return `${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}%`;
}

export function formatPartyKind(kind) {
  const value = String(kind || "").toUpperCase();
  if (value === "SUPPLIER") return "Supplier";
  if (value === "FARMER") return "Farmer";
  if (value === "BROKER") return "Broker";
  if (value === "BUYER") return "Buyer";
  if (value === "DEALER") return "Dealer";
  return value ? value.replaceAll("_", " ") : "Other";
}

export function verdictTone(verdict) {
  const value = String(verdict || "").toUpperCase();
  if (["APPROVED", "PASS", "AUTO_PASS", "RECORDED"].includes(value)) return "success";
  if (["REJECTED", "FAIL"].includes(value)) return "danger";
  if (value) return "warning";
  return "default";
}

export function MajorKpiGrid({ items }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      {items.map((item) => (
        <KpiTile
          key={item.label}
          label={item.label}
          value={item.value}
          sub={item.sub}
          accent={item.accent}
          icon={item.icon}
        />
      ))}
    </div>
  );
}

export function ProgressCard({
  title,
  value,
  subvalue,
  percent,
  leftLabel = "Delivered",
  rightLabel = "Pending",
  tone = "bg-bansal-sage",
}) {
  const safePercent = Math.max(0, Math.min(100, Number(percent || 0)));
  return (
    <SectionCard title={title} className="h-full">
      <div className="space-y-4">
        <div>
          <div className="text-2xl font-serif text-foreground">{value}</div>
          {subvalue ? <div className="text-xs text-muted-foreground mt-1">{subvalue}</div> : null}
        </div>
        <div className="space-y-2">
          <div className="h-3 rounded-full bg-muted/70 overflow-hidden">
            <div className={`h-full ${tone}`} style={{ width: `${safePercent}%` }} />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{leftLabel}</span>
            <span className="font-medium text-foreground">{formatPct(safePercent)}</span>
            <span>{rightLabel}</span>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

export function DonutMeter({ value = 0, label, center, sublabel }) {
  const safe = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div className="flex items-center gap-4">
      <div
        className="relative h-28 w-28 rounded-full"
        style={{
          background: `conic-gradient(#6f8a41 0 ${safe}%, #ece8d8 ${safe}% 100%)`,
        }}
      >
        <div className="absolute inset-3 rounded-full bg-card flex flex-col items-center justify-center text-center">
          <div className="text-xl font-serif text-foreground">{center}</div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        </div>
      </div>
      <div>
        <div className="text-sm font-semibold text-foreground">{formatPct(safe)}</div>
        <div className="text-xs text-muted-foreground">{sublabel}</div>
      </div>
    </div>
  );
}

function ExpenseTable({ rows, drilldown, onRowClick }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <table className="w-full text-sm min-w-[520px]">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground bg-muted border-b border-border">
            <th className="px-3 py-2">Component</th>
            <th className="px-3 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const hasDetail = (drilldown[row.key] || []).length > 0;
            return (
              <tr key={row.key} className="border-b border-border/60">
                <td className="px-3 py-2">
                  {hasDetail ? (
                    <button
                      type="button"
                      onClick={() => onRowClick(row)}
                      className="text-left font-medium link-brand"
                    >
                      {row.label}
                    </button>
                  ) : (
                    row.label
                  )}
                </td>
                <td className={`px-3 py-2 text-right font-medium ${Number(row.amount || 0) < 0 ? "text-red-700" : "text-foreground"}`}>
                  {formatCurrency(row.amount)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ExpenseBreakdownCard({
  rows,
  total,
  perQtl,
  drilldown = {},
  landedCostSection,
  vendorDeductionsSection,
}) {
  const [activeRow, setActiveRow] = useState(null);
  const activeEntries = useMemo(
    () => (activeRow ? drilldown[activeRow.key] || [] : []),
    [activeRow, drilldown],
  );
  const landedRows = landedCostSection?.rows?.length ? landedCostSection.rows : rows;
  const vendorRows = vendorDeductionsSection?.rows || [];
  const showSplitSections = Boolean(landedCostSection?.rows?.length || vendorRows.length);

  return (
    <SectionCard title="Expenses Summary">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          <SummaryStat label="True landed cost" value={formatCurrency(total)} />
          <SummaryStat label="True landed cost / qtl" value={formatCurrency(perQtl)} />
          {vendorDeductionsSection ? (
            <SummaryStat label="Vendor deductions" value={formatCurrency(vendorDeductionsSection.total || 0)} />
          ) : (
            <SummaryStat label="Expense rows" value={rows.length} />
          )}
        </div>

        {showSplitSections ? (
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-foreground">Landed cost components</h3>
                <span className="text-sm font-medium">{formatCurrency(landedCostSection?.total ?? total)}</span>
              </div>
              <ExpenseTable rows={landedRows} drilldown={drilldown} onRowClick={setActiveRow} />
            </div>
            {vendorRows.length ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-foreground">Vendor deductions</h3>
                  <span className="text-sm font-medium text-red-700">
                    {formatCurrency(vendorDeductionsSection?.total || 0)}
                  </span>
                </div>
                <ExpenseTable rows={vendorRows} drilldown={drilldown} onRowClick={setActiveRow} />
              </div>
            ) : null}
          </div>
        ) : (
          <ExpenseTable rows={rows} drilldown={drilldown} onRowClick={setActiveRow} />
        )}
      </div>

      <Dialog open={Boolean(activeRow)} onOpenChange={(open) => !open && setActiveRow(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{activeRow?.label || "Expense detail"}</DialogTitle>
            <DialogDescription>
              Voucher-wise breakdown and calculation basis for {formatCurrency(activeRow?.amount || 0)}.
            </DialogDescription>
          </DialogHeader>
          {activeEntries.length ? (
            <div className="overflow-x-auto rounded-lg border border-border/70">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground bg-muted border-b border-border">
                    <th className="px-3 py-2">Voucher</th>
                    <th className="px-3 py-2">Purchase</th>
                    <th className="px-3 py-2">Calculation</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {activeEntries.map((entry, index) => (
                    <tr key={`${entry.voucher_id}-${index}`} className="border-b border-border/60 align-top">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-medium">{entry.voucher_code}</div>
                        <div className="text-xs text-muted-foreground">{entry.voucher_type || "—"}</div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {entry.purchase_id ? (
                          <Link to={`/purchases/${entry.purchase_id}`} className="link-brand">
                            {entry.purchase_code || entry.purchase_id}
                          </Link>
                        ) : (
                          entry.purchase_code || "—"
                        )}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{entry.calculation || "—"}</td>
                      <td className="px-3 py-2 text-right font-medium whitespace-nowrap">{formatCurrency(entry.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No voucher-level detail available for this head.</p>
          )}
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div className="rounded-lg border border-border/70 bg-background px-3 py-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base font-semibold text-foreground mt-1">{value}</div>
    </div>
  );
}

export function VerdictPill({ verdict }) {
  if (!verdict) return <span className="text-muted-foreground">—</span>;
  return <Pill tone={verdictTone(verdict)}>{String(verdict).replaceAll("_", " ")}</Pill>;
}
